import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radar, RefreshCw, TrendingUp } from 'lucide-react';
import { fetchJSON } from '../lib/api-client';

type WideScanPattern = 'breakout' | 'ma-cross' | 'squeeze';

interface WideScanHit {
  symbol: string;
  code: string;
  name: string;
  board: 'listed' | 'otc';
  pattern: WideScanPattern;
  date: string;
  close: number;
  amount: number;
  note: string;
}

interface WideScanResp {
  pattern: WideScanPattern;
  asOf: string;
  scanned: number;
  hits: WideScanHit[];
  discoveries: number;
  byBoard: { listed: number; otc: number };
  warnings: string[];
  dataSource: string;
  delayed: boolean;
}

interface VerifiedCorrelationItem {
  leader: string;
  target: string;
  label: string;
  significant: boolean;
  sampleDays: number;
  verdict: string;
}

interface VerifiedCorrelationResp {
  date: string;
  items: VerifiedCorrelationItem[];
  significantCount: number;
}

const PATTERN_LABEL: Record<WideScanPattern, string> = {
  breakout: '🚀 突破新高',
  'ma-cross': '📈 均線金叉',
  squeeze: '🎯 收斂縮量',
};

const PATTERN_HINT: Record<WideScanPattern, string> = {
  breakout: '突破前 20 日高 + 量增 + 實體陽線（防誘多）',
  'ma-cross': 'MA5 上穿 MA20 + 放量',
  squeeze: '強漲後高位收斂縮量（突破前夕）',
};

interface Props {
  onSelectSymbol?: (s: string) => void;
}

/**
 * 全市場廣掃（發現層）：台股全市場掃形態，挖出不在自選 / 內建池的冷門候選。
 * 另顯示「已驗證跨板塊關聯」——只露出回測過的事實，不做方向預測（見 §五.16/17）。
 */
export function WideScanPanel({ onSelectSymbol }: Props) {
  const [pattern, setPattern] = useState<WideScanPattern>('breakout');

  const scan = useQuery<WideScanResp>({
    queryKey: ['wide-scan', pattern],
    queryFn: () => fetchJSON<WideScanResp>(`/api/wide-scan?pattern=${pattern}&topN=12`),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const verified = useQuery<VerifiedCorrelationResp>({
    queryKey: ['correlations-verified'],
    queryFn: () => fetchJSON<VerifiedCorrelationResp>('/api/correlations/verified'),
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const hits = scan.data?.hits ?? [];

  return (
    <div className="flex flex-col min-h-0 gap-2">
      {/* 形態切換 */}
      <div className="flex items-center gap-2 flex-wrap">
        {(Object.keys(PATTERN_LABEL) as WideScanPattern[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPattern(p)}
            className={`h-7 rounded border px-2 text-xs ${
              pattern === p
                ? 'border-(--accent) text-(--accent) bg-(--accent)/10'
                : 'border-(--border) text-(--text-secondary) hover:border-(--accent)'
            }`}
            title={PATTERN_HINT[p]}
          >
            {PATTERN_LABEL[p]}
          </button>
        ))}
        <button
          type="button"
          onClick={() => scan.refetch()}
          disabled={scan.isFetching}
          className="h-7 inline-flex items-center gap-1 rounded border border-(--border) px-2 text-xs text-(--text-muted) hover:border-(--accent) disabled:opacity-50"
          title="重新掃描（讀收盤後快取）"
        >
          <RefreshCw className={`h-3 w-3 ${scan.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="text-[10px] text-(--text-muted)">{PATTERN_HINT[pattern]}</div>

      {/* 掃描結果 */}
      {scan.isLoading && (
        <div className="text-(--text-muted) text-sm py-4 text-center">掃描全市場中…</div>
      )}
      {scan.error && !scan.isLoading && (
        <div className="text-(--down) text-xs py-3">廣掃暫時無法取得，稍後再試。</div>
      )}
      {scan.data && hits.length === 0 && !scan.isLoading && (
        <div className="text-(--text-muted) text-xs py-3">
          目前全市場無符合「{PATTERN_LABEL[pattern]}」的個股（掃描 {scan.data.scanned} 檔）。
        </div>
      )}
      {hits.length > 0 && (
        <>
          <div className="text-[10px] text-(--text-muted)">
            掃描 {scan.data?.scanned} 檔 · 命中 {hits.length} · 池外發現 {scan.data?.discoveries} · 資料日 {scan.data?.asOf}（收盤後，延遲）
          </div>
          <ul className="space-y-1">
            {hits.map((h) => (
              <li key={h.symbol}>
                <button
                  type="button"
                  onClick={() => onSelectSymbol?.(h.symbol)}
                  className="w-full text-left p-1.5 rounded bg-(--bg-base) border border-(--border) hover:border-(--accent) flex items-start gap-2"
                >
                  <TrendingUp className="h-3.5 w-3.5 text-(--up) mt-0.5 shrink-0" />
                  <span className="min-w-0">
                    <span className="text-xs font-semibold text-(--text)">
                      {h.symbol} {h.name}
                    </span>
                    <span className="ml-1 text-[10px] text-(--text-muted)">{h.board === 'otc' ? '上櫃' : '上市'}</span>
                    <span className="block text-[11px] text-(--text-secondary) leading-snug">{h.note}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* 已驗證跨板塊關聯（只露已回測的事實，不預測方向） */}
      {verified.data && verified.data.items.length > 0 && (
        <div className="mt-1 pt-2 border-t border-(--border)">
          <div className="text-[11px] font-semibold text-(--accent) mb-1">
            🔬 已驗證關聯 <span className="font-normal text-(--text-muted)">（{verified.data.date} 回測 · 顯著 {verified.data.significantCount}/{verified.data.items.length}）</span>
          </div>
          <ul className="space-y-0.5">
            {verified.data.items.map((it) => (
              <li key={`${it.leader}-${it.target}`} className="text-[10px] text-(--text-muted) leading-snug">
                <span className={it.significant ? 'text-(--warn)' : 'text-(--text-muted)'}>
                  {it.significant ? '⚠ 風險旗標' : '✓ 雜訊'}
                </span>
                {' · '}{it.label}（n={it.sampleDays}）
              </li>
            ))}
          </ul>
          <div className="text-[9px] text-(--text-muted) mt-1 leading-snug">
            「雜訊」= 歷史回測無顯著預測力，別據此預判方向；「風險旗標」也只供降低下注，非買賣訊號。
          </div>
        </div>
      )}
    </div>
  );
}
